import { useCallback, useMemo, useState } from 'react';
import { useOrganizationAppointmentTypes } from '@features/appointment-types';
import { useClinicians } from '@features/organizations';
import { useOrganizationId } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useAvailabilityRulesQuery,
  useCreateAvailabilityRuleMutation,
  useDeleteAvailabilityRuleMutation,
  useUpdateAvailabilityRuleMutation,
} from '../store/api';
import type { AvailabilityEffect, AvailabilityRule } from '../types';
import { ruleToDraft, type ScheduleDraft } from '../utils/schedule';

import {
  AvailabilityManagerView,
  type AvailabilityScopeOption,
  ORG_SCOPE_ID,
} from './AvailabilityManagerView';
import {
  AvailabilityRuleDialog,
  type AvailabilityScheduleInput,
} from './AvailabilityRuleDialog';

interface DialogState {
  mode: 'add' | 'edit';
  effect: AvailabilityEffect;
  initial?: ScheduleDraft;
  editingId?: string | null;
}

/** Container for the availability manager (Direction 1). Fetches the org's
 * rules once and filters by the selected scope client-side (rule sets are
 * small, and there's no server filter for "org-wide only"). The settings page
 * is gated on `manage_org_calendar`, so every scope — clinic default and any
 * clinician — is editable here. Add/edit go through a focused dialog. */
export function AvailabilitySection() {
  const organizationId = useOrganizationId();
  const {
    data,
    isLoading: rulesLoading,
    error,
  } = useAvailabilityRulesQuery({ perPage: 200 });
  const { clinicians, isLoading: cliniciansLoading } = useClinicians();
  const { appointmentTypes } = useOrganizationAppointmentTypes(organizationId);
  const [createRule] = useCreateAvailabilityRuleMutation();
  const [updateRule] = useUpdateAvailabilityRuleMutation();
  const [deleteRule] = useDeleteAvailabilityRuleMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { schedulingSettings, error: errorTracking } = useEventTracking();

  const [scopeId, setScopeId] = useState<string>(ORG_SCOPE_ID);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const scopes: AvailabilityScopeOption[] = useMemo(
    () => [
      { id: ORG_SCOPE_ID, label: 'Clinic default', kind: 'org' },
      ...clinicians.map(c => ({
        id: c.userId,
        label: c.name,
        kind: 'clinician' as const,
      })),
    ],
    [clinicians],
  );

  const encounterTypes = useMemo(
    () =>
      appointmentTypes.map(t => ({
        id: t.encounter_type_id,
        name: t.encounter_type,
      })),
    [appointmentTypes],
  );

  // Rules for the selected scope; the view narrows further by encounter type.
  const scopeRules = useMemo(() => {
    const all = data?.items ?? [];
    return all.filter(rule =>
      scopeId === ORG_SCOPE_ID
        ? rule.provider_id == null
        : rule.provider_id === scopeId,
    );
  }, [data?.items, scopeId]);

  const scopeLabel =
    scopes.find(s => s.id === scopeId)?.label ?? 'Clinic default';
  const encounterTypeName = typeId
    ? (encounterTypes.find(t => t.id === typeId)?.name ?? 'Appointment type')
    : 'All types';

  const handleSubmit = useCallback(
    async (schedule: AvailabilityScheduleInput) => {
      if (!dialog) return;
      setIsSaving(true);
      try {
        if (dialog.mode === 'edit' && dialog.editingId) {
          await updateRule({
            availabilityRuleId: dialog.editingId,
            updateAvailabilityRuleRequest: schedule,
          }).unwrap();
          schedulingSettings.availabilityRuleChanged({
            action: 'updated',
            effect: dialog.effect,
          });
          enqueueSnackbar('Availability updated', { variant: 'success' });
        } else {
          await createRule({
            createAvailabilityRuleRequest: {
              effect: dialog.effect,
              // Explicit null = org-wide; a clinician id scopes to them.
              provider_id: scopeId === ORG_SCOPE_ID ? null : scopeId,
              encounter_type_id: typeId,
              ...schedule,
            },
          }).unwrap();
          schedulingSettings.availabilityRuleChanged({
            action: 'created',
            effect: dialog.effect,
          });
          enqueueSnackbar('Availability added', { variant: 'success' });
        }
        setDialog(null);
      } catch {
        enqueueSnackbar('Couldn’t save availability', { variant: 'error' });
        errorTracking.track(
          'Could not save availability',
          'availability_rule_save',
        );
      } finally {
        setIsSaving(false);
      }
    },
    [
      dialog,
      scopeId,
      typeId,
      createRule,
      updateRule,
      schedulingSettings,
      enqueueSnackbar,
      errorTracking,
    ],
  );

  const handleDelete = useCallback(
    async (rule: AvailabilityRule) => {
      const id = rule.availability_rule_id;
      if (!id) return;
      setDeletingId(id);
      try {
        await deleteRule({ availabilityRuleId: id }).unwrap();
        schedulingSettings.availabilityRuleChanged({
          action: 'deleted',
          effect: rule.effect as AvailabilityEffect,
        });
        enqueueSnackbar('Availability removed', { variant: 'success' });
      } catch {
        enqueueSnackbar('Couldn’t remove availability', { variant: 'error' });
        errorTracking.track(
          'Could not remove availability',
          'availability_rule_delete',
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deleteRule, schedulingSettings, enqueueSnackbar, errorTracking],
  );

  return (
    <>
      <AvailabilityManagerView
        scopes={scopes}
        selectedScopeId={scopeId}
        onScopeChange={setScopeId}
        encounterTypes={encounterTypes}
        selectedEncounterTypeId={typeId}
        onEncounterTypeChange={setTypeId}
        rules={scopeRules}
        isLoading={rulesLoading || cliniciansLoading}
        hasError={!!error}
        deletingId={deletingId}
        onAddHours={() => setDialog({ mode: 'add', effect: 'open' })}
        onAddBlock={() => setDialog({ mode: 'add', effect: 'block' })}
        onEdit={rule =>
          setDialog({
            mode: 'edit',
            effect: rule.effect as AvailabilityEffect,
            initial: ruleToDraft(rule),
            editingId: rule.availability_rule_id,
          })
        }
        onDelete={handleDelete}
      />
      <AvailabilityRuleDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'add'}
        effect={dialog?.effect ?? 'open'}
        scopeLabel={scopeLabel}
        encounterTypeName={encounterTypeName}
        initial={dialog?.initial}
        isSaving={isSaving}
        onClose={() => setDialog(null)}
        onSubmit={handleSubmit}
      />
    </>
  );
}
